import React, {
    useMemo,
    useState,
    useRef,
    useEffect,
    useCallback,
} from 'react';
import { Popover, Input, Tag, Icon, Spin } from 'antd';
import classname from 'classname';
import debounce from 'lodash/debounce';
import './index.scss';

const SEARCH_DEBOUNCE = 300;
const DEFAULT_MAX_SELECTION = 3;

export interface IPositionSelectorProps {
    value?: string[];
    defaultValue?: string[];
    onChange?: (positions: string[]) => void;
    onReachMax?: (max: number) => void;
    fetchSuggestions: (keyword: string) => Promise<string[]>;
    maxSelection?: number;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    style?: React.CSSProperties;
    maxTagCount?: number;
    overlayClassName?: string;
    footerTip?: string;
}

const sanitizeList = (list: unknown[]) =>
    Array.from(
        new Set(
            list
                .map(item => {
                    if (typeof item === 'string') {
                        return item.trim();
                    }
                    if (typeof item === 'number') {
                        return String(item).trim();
                    }
                    return '';
                })
                .filter(Boolean),
        ),
    );

export const PositionSelector: React.FC<IPositionSelectorProps> = ({
    value,
    defaultValue = [],
    onChange,
    onReachMax,
    fetchSuggestions,
    maxSelection = DEFAULT_MAX_SELECTION,
    placeholder = '请选择职位（最多 3 个）',
    disabled = false,
    className,
    style,
    maxTagCount,
    overlayClassName,
    footerTip,
}) => {
    const isControlled = typeof value !== 'undefined';
    const [innerValue, setInnerValue] = useState<string[]>(
        sanitizeList(defaultValue),
    );
    const mergedValue = (isControlled
        ? sanitizeList(value || [])
        : innerValue) as string[];

    const [visible, setVisible] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [keyword, setKeyword] = useState('');
    const [options, setOptions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const fetchIdRef = useRef(0);
    const searchInputRef = useRef<Input | null>(null);
    const debouncedSetKeywordRef = useRef<ReturnType<typeof debounce>>();
    const isComposingRef = useRef(false);

    useEffect(() => {
        if (!isControlled) {
            setInnerValue(sanitizeList(defaultValue));
        }
    }, [defaultValue, isControlled]);

    const updateValue = useCallback(
        (next: string[]) => {
            const sanitized = sanitizeList(next);
            if (!isControlled) {
                setInnerValue(sanitized);
            }
            onChange && onChange(sanitized);
        },
        [isControlled, onChange],
    );

    const selectedSet = useMemo(() => new Set(mergedValue), [mergedValue]);

    useEffect(() => {
        if (!visible) {
            return;
        }
        searchInputRef.current?.focus();
    }, [visible]);

    if (!debouncedSetKeywordRef.current) {
        debouncedSetKeywordRef.current = debounce((next: string) => {
            setKeyword(next);
        }, SEARCH_DEBOUNCE);
    }

    useEffect(() => {
        const debounced = debouncedSetKeywordRef.current;
        return () => {
            debounced?.cancel();
        };
    }, []);

    useEffect(() => {
        const trimmed = keyword.trim();
        if (!trimmed) {
            setOptions([]);
            setLoading(false);
            return;
        }
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        fetchSuggestions(trimmed)
            .then(result => {
                if (fetchId !== fetchIdRef.current) {
                    return;
                }
                setOptions(sanitizeList(result));
            })
            .catch(() => {
                if (fetchId !== fetchIdRef.current) {
                    return;
                }
                setOptions([]);
            })
            .finally(() => {
                if (fetchId === fetchIdRef.current) {
                    setLoading(false);
                }
            });
    }, [keyword, fetchSuggestions]);

    const handleToggle = useCallback(
        (position: string) => {
            if (disabled) {
                return;
            }
            const trimmed = position.trim();
            if (!trimmed) {
                return;
            }
            const nextSet = new Set(selectedSet);
            if (nextSet.has(trimmed)) {
                nextSet.delete(trimmed);
            } else {
                if (maxSelection > 0 && nextSet.size >= maxSelection) {
                    onReachMax && onReachMax(maxSelection);
                    return;
                }
                nextSet.add(trimmed);
            }
            updateValue(Array.from(nextSet));
        },
        [disabled, selectedSet, maxSelection, onReachMax, updateValue],
    );

    const handleRemove = useCallback(
        (position: string) => {
            if (disabled) {
                return;
            }
            const next = mergedValue.filter(item => item !== position);
            updateValue(next);
        },
        [disabled, mergedValue, updateValue],
    );

    const handleSearchInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            setSearchValue(nextValue);
            if (!isComposingRef.current) {
                debouncedSetKeywordRef.current?.(nextValue);
            }
        },
        [],
    );

    const handleAddCustom = useCallback(() => {
        const trimmed = searchValue.trim();
        if (!trimmed) {
            return;
        }
        handleToggle(trimmed);
        debouncedSetKeywordRef.current?.cancel();
        setSearchValue('');
        setKeyword('');
        setOptions([]);
    }, [handleToggle, searchValue]);

    const renderTags = () => {
        if (!mergedValue.length) {
            return (
                <span className="crm-position-selector-placeholder">
                    {placeholder}
                </span>
            );
        }
        const list =
            typeof maxTagCount === 'number' && maxTagCount >= 0
                ? mergedValue.slice(0, maxTagCount)
                : mergedValue;

        return (
            <>
                {list.map(position => (
                    <Tag
                        key={position}
                        closable={!disabled}
                        onClose={(event: React.MouseEvent<HTMLElement>) => {
                            event.preventDefault();
                            handleRemove(position);
                        }}
                    >
                        {position}
                    </Tag>
                ))}
                {mergedValue.length > list.length ? (
                    <span className="crm-position-selector-more">
                        +{mergedValue.length - list.length}
                    </span>
                ) : null}
            </>
        );
    };

    const trimmedSearch = searchValue.trim();
    const canAddCustom =
        !!trimmedSearch &&
        !selectedSet.has(trimmedSearch) &&
        !options.includes(trimmedSearch);

    const tipText =
        footerTip ||
        `请选择职位，最多可选择${maxSelection > 0 ? maxSelection : '不限'}个`;

    const overlayContent = (
        <div
            className="crm-position-selector-dropdown"
            onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                event.stopPropagation()
            }
        >
            <div className="crm-position-selector-search">
                <Input
                    ref={searchInputRef}
                    value={searchValue}
                    placeholder="请输入职位关键字"
                    allowClear
                    onChange={handleSearchInputChange}
                    onCompositionStart={() => {
                        isComposingRef.current = true;
                    }}
                    onCompositionEnd={(
                        event: React.CompositionEvent<HTMLInputElement>,
                    ) => {
                        isComposingRef.current = false;
                        const nextValue = event.currentTarget.value;
                        setSearchValue(nextValue);
                        debouncedSetKeywordRef.current?.(nextValue);
                    }}
                />
            </div>
            <div className="crm-position-selector-list">
                {loading ? (
                    <div className="crm-position-selector-empty">
                        <Spin size="small" />
                    </div>
                ) : options.length === 0 ? (
                    <div className="crm-position-selector-empty">
                        {trimmedSearch
                            ? `未找到“${trimmedSearch}”相关职位`
                            : '请输入关键字搜索职位'}
                    </div>
                ) : (
                    options.map(option => {
                        const isSelected = selectedSet.has(option);
                        return (
                            <div
                                key={option}
                                className={classname(
                                    'crm-position-selector-item',
                                    {
                                        selected: isSelected,
                                        disabled,
                                    },
                                )}
                                onClick={() => handleToggle(option)}
                            >
                                <span className="text">{option}</span>
                                {isSelected ? <Icon type="check" /> : null}
                            </div>
                        );
                    })
                )}
                {canAddCustom ? (
                    <div
                        className="crm-position-selector-item add"
                        onClick={handleAddCustom}
                    >
                        添加“{trimmedSearch}”
                        <Icon type="plus" />
                    </div>
                ) : null}
            </div>
            <div className="crm-position-selector-selected">
                {mergedValue.length ? (
                    mergedValue.map(position => (
                        <Tag
                            key={position}
                            closable={!disabled}
                            onClose={(event: React.MouseEvent<HTMLElement>) => {
                                event.preventDefault();
                                handleRemove(position);
                            }}
                        >
                            {position}
                        </Tag>
                    ))
                ) : (
                    <span className="crm-position-selector-selected-placeholder">
                        尚未选择职位
                    </span>
                )}
            </div>
            <div className="crm-position-selector-footer">
                <span className="tip">
                    {tipText}
                    <span className="count">
                        （已选 {mergedValue.length}
                        {maxSelection > 0 ? `/${maxSelection}` : ''}）
                    </span>
                </span>
            </div>
        </div>
    );

    return (
        <Popover
            overlayClassName={classname(
                'crm-position-selector-popover',
                overlayClassName,
            )}
            placement="bottomLeft"
            trigger="click"
            visible={visible && !disabled}
            onVisibleChange={next => {
                if (disabled) {
                    return;
                }
                setVisible(next);
                if (!next) {
                    debouncedSetKeywordRef.current?.cancel();
                    setKeyword('');
                    setSearchValue('');
                    isComposingRef.current = false;
                    setOptions([]);
                    setLoading(false);
                }
            }}
            content={overlayContent}
        >
            <div
                className={classname(
                    'crm-position-selector-trigger',
                    className,
                    {
                        disabled,
                        open: visible,
                        empty: mergedValue.length === 0,
                    },
                )}
                style={style}
                onClick={() => {
                    if (!disabled) {
                        setVisible(prev => !prev);
                    }
                }}
            >
                <div className="crm-position-selector-value">
                    {renderTags()}
                </div>
                <div className="crm-position-selector-controls">
                    {mergedValue.length > 0 && !disabled ? (
                        <Icon
                            type="close-circle"
                            theme="filled"
                            className="crm-position-selector-action"
                            onClick={(event: React.MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                updateValue([]);
                            }}
                        />
                    ) : null}
                    <Icon
                        type={visible ? 'up' : 'down'}
                        className="crm-position-selector-action arrow"
                    />
                </div>
            </div>
        </Popover>
    );
};

export default PositionSelector;
